import axiosInstance from '../services/AxiosInstance';
import { cachedRequest, clearCache } from './requestCache';

const API_URL = '/trainers';
const TRAINERS_PAGE_CAP = 100;

const normalizeTrainer = (trainer) => {
  if (!trainer || typeof trainer !== 'object') return trainer;
  const userId = trainer.userId || trainer.trainerId || '';
  return {
    ...trainer,
    userId,
  };
};

// Get all trainers with optional pagination and search (unchanged)
export const getTrainers = async (limit = 20, page = 1, search = '', searchField="") => {
  try {
    const params = {
      limit: Number(limit),
      page: Number(page),
    };

    if(searchField && searchField !== "all") {
      params.search = search.trim();
      params.searchField = searchField;
    } 
    
    if (search && search.trim() !== '') {
      params.search = search.trim();
    }
    
    const response = await axiosInstance.get(API_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching trainers', error);
    throw error;
  }
};

/**
 * One page of trainers for a picker: { id, trainerName, trainerEmail } per
 * row, `id` being the trainer record's own id. Same search, role scoping
 * and paging as getTrainers, without the rest of each record.
 */
export const getTrainerOptions = async (limit = 20, page = 1, search = '') => {
  const params = { limit: Number(limit), page: Number(page), fields: 'options' };
  if (search && search.trim() !== '') params.search = search.trim();
  const response = await axiosInstance.get(API_URL, { params });
  return response.data;
};

export const fetchAllTrainers = async (search = '') => {
  const safeSearch = search || '';
  return cachedRequest(`trainers:all:${safeSearch}`, 60000, async () => {
    const all = [];
    let page = 1;
    const maxPages = 500;
    while (page <= maxPages) {
      // Full pages, as fetchAllBatches does: this walk wants everyone, and
      // ten at a time is twenty round-trips for a couple of hundred
      // trainers. The server clamps anything above this anyway.
      const safeLimit = TRAINERS_PAGE_CAP;
      const pageLocal = page;
      const res = await cachedRequest(
        `trainers:list:lite:${safeLimit}:${pageLocal}:${safeSearch}`,
        60000,
        async () => {
          const response = await axiosInstance.get(
            `${API_URL}?limit=${safeLimit}&page=${pageLocal}&search=${safeSearch}&includeTotal=false`
          );
          return {
            data: Array.isArray(response.data.data) ? response.data.data.map(normalizeTrainer) : [],
            pagination: response.data.pagination,
          };
        }
      );
      const chunk = Array.isArray(res?.data) ? res.data : [];
      if (!chunk.length) break;
      all.push(...chunk);
      const hasNext = Boolean(res?.pagination?.hasNext);
      // totalPages is null when the list is asked for without a total --
      // which is exactly how this loop asks. Read as `|| 1` that made
      // page 1 the last page every time, so the picker only ever saw the
      // first ten trainers. An unknown count is not a count of one: let
      // hasNext and a short page end the walk instead.
      const totalPages = Number.parseInt(res?.pagination?.totalPages, 10);
      const atLastPage = Number.isFinite(totalPages) && page >= totalPages;
      if (!hasNext || atLastPage || chunk.length < safeLimit) break;
      page += 1;
    }
    return all;
  });
};

/** Every trainer as { id, trainerName, trainerEmail } -- fetchAllTrainers for a picker's "Select all". */
export const fetchAllTrainerOptions = async (search = '') => {
  const all = [];
  let page = 1;
  const maxPages = 500;
  while (page <= maxPages) {
    const res = await getTrainerOptions(TRAINERS_PAGE_CAP, page, search);
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    const hasNext = Boolean(res?.pagination?.hasNext);
    const totalPages = Number.parseInt(res?.pagination?.totalPages, 10);
    const atLastPage = Number.isFinite(totalPages) && page >= totalPages;
    if (!hasNext || atLastPage || chunk.length < TRAINERS_PAGE_CAP) break;
    page += 1;
  }
  return all;
};

// Create a new trainer
export const createTrainer = async (trainerData) => {
  try {
    const response = await axiosInstance.post(API_URL, trainerData);
    clearCache('trainers:');
    return response.data;
  } catch (error) {
    console.error('Error creating trainer', {
      error: error.response?.data,
      requestData: trainerData,
    });
    throw error;
  }
};

// Update a trainer by ID
export const updateTrainer = async (trainerId, trainerData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${trainerId}`, trainerData);
    clearCache('trainers:');
    return response.data;
  } catch (error) {
    console.error('Error updating trainer', error);
    throw error;
  }
};

// Delete a trainer by ID
export const deleteTrainer = async (trainerId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${trainerId}`);
    clearCache('trainers:');
    return response.data;
  } catch (error) {
    console.error('Error deleting trainer', error);
    throw error;
  }
};

// Get a trainer by ID (unchanged, base version)
export const getTrainerById = async (trainerId) => {
  return cachedRequest(`trainers:byId:${trainerId}`, 60000, async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/${trainerId}`);
      return {
        data: normalizeTrainer(response.data.data),
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error fetching trainer details', error);
      throw error;
    }
  });
};

// Get a trainer by trainerId using new external endpoint (added as requested)
export const getTrainerByTrainerId = async (trainerId) => {
  return cachedRequest(`trainers:byTrainerId:${trainerId}`, 60000, async () => {
    try {
      const response = await axiosInstance.get(`/trainers/trainer/${trainerId}`);
      return {
        data: normalizeTrainer(response.data.data),
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Error fetching trainer details', error);
      throw error;
    }
  });
};

export const getTrainerByUserId = async (userId) => {
  return getTrainerByTrainerId(userId);
};
